import React from 'react';
import type { z } from 'zod';
import { Button, HelpIcon, Tabs, Textarea } from '../../components/ui';
import { Form } from '../../components/form';
import { zWorkflowStepInput } from '../../generated/api/zod.gen.js';
import { BuilderTextareaField } from './BuilderTextareaField.js';
import { useBuilderForm } from './useBuilderForm.js';
import { HELP } from './WorkflowBuilderTypes.js';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';
import { StepTemplatePicker } from './StepTemplatePicker.js';
import type { AgentTemplateSummary } from '../../generated/api/types.gen.js';
import type { PreviewPromptRole } from './promptPreview.types.js';

interface PromptPreview {
  systemPrompt: string;
  userPrompt: string;
  estimatedTokens: number;
  role: PreviewPromptRole;
}

interface StepPromptTabProps {
  step: WorkflowBuilderStep;
  roles: AgentTemplateSummary[];
  promptPreview: PromptPreview | null;
  promptLoading: boolean;
  instructionMode: 'freetext' | 'template';
  onInstructionModeChange: (mode: 'freetext' | 'template') => void;
  onUpdateStep: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
  onOpenPromptBuilder?: () => void;
}

const INSTRUCTION_MODES = [
  { id: 'freetext', label: 'Freetext' },
  { id: 'template', label: 'Template' },
];

/** The two prompt texts this tab edits, from the generated step schema. */
const promptSchema = zWorkflowStepInput.pick({ systemPromptOverride: true, additionalInstructions: true });
type PromptValues = z.input<typeof promptSchema>;

export const StepPromptTab: React.FC<StepPromptTabProps> = ({
  step,
  roles,
  promptPreview,
  promptLoading,
  instructionMode,
  onInstructionModeChange,
  onUpdateStep,
}) => {
  const stepId = step.id.toString();
  const fields = useBuilderForm(
    promptSchema,
    {
      systemPromptOverride: step.systemPromptOverride ?? '',
      additionalInstructions: step.additionalInstructions ?? '',
    },
    (field, all) => {
      if (field === 'systemPromptOverride')
        onUpdateStep(stepId, { systemPromptOverride: all.systemPromptOverride ?? '' });
      if (field === 'additionalInstructions') {
        const text = all.additionalInstructions ?? '';
        onUpdateStep(stepId, { additionalInstructions: text, userPromptTemplate: text });
      }
    },
  );
  return (
    <Form form={fields} onSubmit={() => undefined} className="flex-1 min-h-0 gap-3">
      {/* Agent Template selector */}
      <div className="flex items-start gap-3 shrink-0">
        <div className="flex-1">
          <label className="block text-xs font-medium text-muted mb-1">Agent template</label>
          <StepTemplatePicker
            step={step}
            roles={roles}
            onSelect={(r) =>
              onUpdateStep(step.id.toString(), {
                templateId: r?.id ?? null,
                templateSlug: r?.slug ?? null,
                systemPromptOverride: null,
              })
            }
          />
        </div>
        {promptPreview && <span className="text-[10px] text-faint mt-5">Model: {promptPreview.role.model}</span>}
      </div>

      {!step.templateSlug ? (
        <div className="text-center py-8 text-muted text-sm">Select an agent template above to configure prompts.</div>
      ) : (
        <>
          {/* System Prompt */}
          <div className="flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between mb-1 shrink-0">
              <label className="text-xs font-medium text-muted">
                System prompt <HelpIcon text={HELP.systemPrompt} />
              </label>
              {promptPreview && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    onUpdateStep(step.id.toString(), {
                      systemPromptOverride: step.systemPromptOverride ? undefined : promptPreview.systemPrompt,
                    })
                  }
                >
                  {step.systemPromptOverride ? 'Reset to role default' : 'Customize'}
                </Button>
              )}
            </div>
            {step.systemPromptOverride !== null && step.systemPromptOverride !== '' ? (
              <BuilderTextareaField<PromptValues>
                name="systemPromptOverride"
                aria-label="System prompt"
                className="border-accent"
                placeholder="Enter custom system prompt. This defines the agent's identity, expertise, and rules for this specific step."
              />
            ) : (
              <pre className="flex-1 min-h-[60px] px-2 py-1.5 bg-bg border rounded-sm text-xs text-muted font-mono overflow-auto whitespace-pre-wrap">
                {promptLoading
                  ? 'Loading...'
                  : (promptPreview?.systemPrompt ??
                    'Select a role in the Config tab to see the default system prompt.')}
              </pre>
            )}
          </div>

          {/* Mode toggle */}
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted">
                Step instructions{' '}
                <HelpIcon
                  text={instructionMode === 'template' ? HELP.agentInstructionTemplate : HELP.agentInstructionFreetext}
                />
              </label>
            </div>
            <Tabs
              variant="segmented"
              aria-label="Instruction mode"
              tabs={INSTRUCTION_MODES}
              activeTab={instructionMode}
              onChange={(mode) => {
                if (mode === 'freetext') {
                  // Save current content to template slot before switching
                  if (instructionMode === 'template') {
                    onUpdateStep(step.id.toString(), {
                      userPromptWithVars: step.additionalInstructions ?? step.userPromptTemplate,
                      promptMode: 'freetext',
                      additionalInstructions: step.userPromptFreetext ?? '',
                      userPromptTemplate: step.userPromptFreetext ?? '',
                    });
                  } else {
                    onUpdateStep(step.id.toString(), { promptMode: 'freetext' });
                  }
                  onInstructionModeChange('freetext');
                } else if (mode === 'template') {
                  // Save current content to freetext slot before switching
                  if (instructionMode === 'freetext') {
                    onUpdateStep(step.id.toString(), {
                      userPromptFreetext: step.additionalInstructions ?? step.userPromptTemplate,
                      promptMode: 'template',
                      additionalInstructions: step.userPromptWithVars ?? '',
                      userPromptTemplate: step.userPromptWithVars ?? '',
                    });
                  } else {
                    onUpdateStep(step.id.toString(), { promptMode: 'template' });
                  }
                  onInstructionModeChange('template');
                }
              }}
            />
          </div>

          {/* Freetext: single textarea fills remaining space */}
          {instructionMode === 'freetext' && (
            <div className="flex flex-col flex-1 min-h-0">
              <BuilderTextareaField<PromptValues>
                name="additionalInstructions"
                aria-label="Step instructions"
                placeholder={
                  'Write the complete instruction for the agent.\nThis text is sent exactly as written — no variable substitution.\n\nExample:\nAnalyze the current US macro environment focusing on interest rates,\ninflation trends, and employment data. Provide a regime classification\n(expansion, contraction, or stagflation) with a confidence level.\nInclude the key data points that support your assessment.\nFormat the output as JSON with fields: regime, confidence, key_indicators.\n\nBenefits: Simple and direct. Best for one-off steps.\nWhat you write is exactly what the agent receives.'
                }
              />
            </div>
          )}

          {/* Template: instruction + variables side by side */}
          {instructionMode === 'template' && (
            <div className="flex flex-col lg:flex-row flex-1 min-h-0 gap-3">
              {/* Instruction (left 50%) */}
              <div className="flex flex-col flex-1 min-h-0 min-w-0">
                <label className="text-[10px] text-faint mb-1 shrink-0">Instruction</label>
                <BuilderTextareaField<PromptValues>
                  name="additionalInstructions"
                  aria-label="Instruction"
                  className="border-warning"
                  placeholder={
                    'Write your instruction using {{variable}} placeholders.\nPlaceholders are replaced with values from Variables at runtime.\n\nExample:\nAnalyze the macro environment for {{regions}}.\nFocus on: {{focus_areas}}.\nUse data from the last {{lookback_days}} days.\n\nBenefits: Reusable across different parameters.\nClone the workflow and change only the Variables.'
                  }
                />
              </div>
              {/* Variables (right 50%) */}
              <div className="flex flex-col flex-1 min-h-0 min-w-0">
                <div className="flex items-center justify-between mb-1 shrink-0">
                  <label className="text-[10px] text-faint">
                    Variables <HelpIcon text={HELP.variables} />
                  </label>
                  <span className="text-[10px] text-faint">
                    {(() => {
                      const matches = step.additionalInstructions?.match(/\{\{(\w+)\}\}/g) || [];
                      return matches
                        .map((m) => m.replace(/\{|\}/g, ''))
                        .filter((v, i, a) => a.indexOf(v) === i)
                        .map((k) => (
                          <code key={k} className="px-0.5 rounded-sm mr-1 bg-surface-alt text-muted">
                            {k}
                          </code>
                        ));
                    })()}
                  </span>
                </div>
                <Textarea
                  value="{}"
                  disabled
                  title="Config has been migrated to individual step fields"
                  className="flex-1 min-h-[60px] font-mono text-xs"
                  placeholder={
                    '{\n  "regions": ["US", "EU", "Asia"],\n  "focus_areas": ["rates", "inflation"],\n  "lookback_days": 90\n}\n\nKeys must match {{placeholders}} in the instruction.\nGreen = defined, Red = missing.'
                  }
                />
              </div>
            </div>
          )}
        </>
      )}
    </Form>
  );
};
